import { useMemo } from 'react';

import {
  type BannerListItemViewModel,
  type GetApiV1BannerQueryParams,
  useGetApiV1Banner,
} from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import { BANNER_STATUS_FILTER, type BannerFilters } from '../types/bannerFilters';

/** Itens por página da listagem de Banner (paginação server-side). */
export const BANNER_PAGE_SIZE = 20;

/** Envelope que o backend usa em algumas listagens, mesmo quando o tipo gerado promete outro formato. */
interface PagedEnvelope {
  readonly data?: BannerListItemViewModel[] | null;
  readonly currentPage?: number;
  readonly totalPages?: number;
  readonly totalCount?: number;
}

/**
 * Normaliza a resposta da listagem antes de iterar.
 *
 * Os tipos do Kubb prometem `BannerListItemViewModelPaginatedResponse`, mas o
 * backend deste projeto às vezes devolve o array puro — o mesmo desencontro que
 * motivou `unwrapPagedResponse`. Iterar direto quebra em runtime
 * (`X.map is not a function`) e passa nos testes, porque os handlers MSW
 * gerados devolvem array puro.
 */
function unpackResponse(raw: unknown): {
  items: BannerListItemViewModel[];
  totalCount: number;
  totalPages: number;
  currentPage: number | undefined;
} {
  const items = unwrapPagedResponse<BannerListItemViewModel>(raw);
  if (Array.isArray(raw)) {
    return { items, totalCount: items.length, totalPages: 1, currentPage: undefined };
  }

  const paged = raw as PagedEnvelope | undefined;
  return {
    items,
    totalCount: paged?.totalCount ?? items.length,
    totalPages: paged?.totalPages ?? 1,
    currentPage: paged?.currentPage,
  };
}

/** Monta os query params a partir dos filtros aplicados. */
function buildApiParams(filters: BannerFilters): GetApiV1BannerQueryParams {
  return {
    ...(filters.search ? { title: filters.search } : {}),
    ...(filters.status !== BANNER_STATUS_FILTER.All
      ? { active: filters.status === BANNER_STATUS_FILTER.Active }
      : {}),
    ...(filters.companyId !== undefined ? { companyId: filters.companyId } : {}),
    page: filters.page,
    pageSize: BANNER_PAGE_SIZE,
  };
}

/**
 * Hook para listar Banners com filtros e paginação **server-side**.
 *
 * Envolve o hook gerado pelo Kubb para `GET /api/v1/banner`, traduzindo os
 * filtros da feature para os query params do endpoint (`title`, `active`,
 * `companyId`, `page`, `pageSize`) e normalizando a resposta.
 *
 * @param filters - Filtros de busca/paginação atuais
 * @returns Página de banners com estado de loading/error e os totais da API
 *
 * @example
 * ```tsx
 * const { banners, totalCount, isLoading } = useBannerList(filters);
 * ```
 */
export function useBannerList(filters: BannerFilters) {
  const query = useGetApiV1Banner(buildApiParams(filters));

  const { items, totalCount, totalPages, currentPage } = useMemo(
    () => unpackResponse(query.data),
    [query.data],
  );

  return {
    banners: items,
    totalCount,
    totalPages,
    currentPage: currentPage ?? filters.page,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}
